import { Button, TextField } from '@warp-ds/react';
import React, { useMemo, useState } from 'react';
import ProgressBar from './ProgressBar';
import PriceCalculatorInfoExpandable from './PriceCalculatorInfoExpandable';
import {
  calculateBreakdown, calculatedDiscount, priceTable,
} from './motorPriceCalcUtil';
import { fixToPositiveIntegerString, formatToNorwegianPrice } from '../../../utils/priceUtil';
// PROD-2739 This file can be deleted when Valere goes live 1/1/25

function CalculatedDiscount({ input }: { input: number }) {
  const { totalPrice, currentStep } = useMemo(() => calculateBreakdown(input), [input]);
  const discount = useMemo(() => calculatedDiscount(input, input ? (totalPrice / input) : totalPrice), [totalPrice, input]);
  const averagePrice = useMemo(() => Math.round(input ? totalPrice / input : totalPrice), [totalPrice, input]);

  return (
    <span id="result" className="flex flex-col items-start gap-8 self-stretch">
      <div className="text-primary text-caption">
        Forventet pris ut fra
        {' '}
        <span className="font-bold">{`${formatToNorwegianPrice(input)} annonser`}</span>
      </div>
      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-40 self-stretch">
        <div className="flex items-center gap-40">
          <div>
            <div className="t2">{`${formatToNorwegianPrice(averagePrice)} kr`}</div>
            <span className="text-caption">per annonse</span>
          </div>
          <div>
            <div className="t2">{`${discount} %`}</div>
            <span className="text-caption">rabatt</span>
          </div>
        </div>
        <div className="flex flex-col h-[52px] justify-center items-start gap-4 self-stretch">
          <span id="step-indicator-label">
            Pristrinn
            {' '}
            <span className="font-bold">{`${currentStep} av ${priceTable.length}`}</span>
          </span>
          <ProgressBar aria-hidden aria-describedby="step-indicator-label" totalSteps={priceTable.length} currentStep={currentStep - 1} />
        </div>
      </div>
    </span>
  );
}

type MotorPriceCalculatorProps = {
  numberOfPublishedAds?: number,
};

export default function MotorPriceCalculator({ numberOfPublishedAds }: MotorPriceCalculatorProps) {
  const loggedIn = numberOfPublishedAds !== undefined;
  const [inputText, setInputText] = useState(loggedIn ? `${numberOfPublishedAds}` : '');
  const [inputValue, setInputValue] = useState(loggedIn ? numberOfPublishedAds : undefined);
  const [invalidText, setInvalidText] = useState('');

  return (
    <>
      <div className="flex flex-col items-start gap-24 p-32 border rounded">
        <div>
          <h4>Priskalkulator til Leasingbil, bruktbil og nybil</h4>
          Få en estimert pris per annonse ved å legge inn hvor mange annonser bedriften din publiserer ila. et år. Rabatten øker etter hvert som du publiserer flere annonser.
        </div>
        <form
          id="calcForm"
          onSubmit={(e) => {
            e.preventDefault();

            if (inputText.length > 0) {
              setInputValue(Number(inputText));
            } else {
              setInvalidText('Skriv inn et helt tall fra 0 og opp');
              setInputValue(undefined);
            }
          }}
        >
          <div className="flex flex-wrap items-stretch sm:items-start gap-12">
            <TextField
              className="w-full !sm:w-[231px]"
              type="number"
              id="priceInput"
              maxLength={15}
              min={0}
              aria-label="Antall annonser"
              helpText={invalidText || (
                loggedIn ? (
                  <p className="s-text">
                    <span className="font-bold">{numberOfPublishedAds}</span>
                    {' '}
                    annonser lagt ut siste 12 måneder
                  </p>
                ) : undefined
              )}
              invalid={!!invalidText}
              placeholder="Antall annonser ila. et år"
              value={inputText}
              onChange={(e) => {
                const result = fixToPositiveIntegerString(e.target.value);
                setInputText(result);
                if (result !== '') setInvalidText('');
              }}
            />
            <Button className="h-48 p-8 whitespace-nowrap min-w-full sm:min-w-max" primary id="calculate" type="submit">Beregn pris</Button>
          </div>
        </form>
        {(inputValue !== undefined) && <CalculatedDiscount input={inputValue} />}
      </div>
      <PriceCalculatorInfoExpandable />
    </>
  );
}
